// @ts-nocheck -- staged migration: add explicit types before removing this directive.
// Deterministically picks one feature to recommend on the homepage's
// "Continue learning" section, from the progress signals the app already
// tracks (kanji memory quiz, kanji/kana writing reps, grammar and vocabulary
// "learned" marks). Pure and side-effect free so it's easy to unit test -
// see useContinueLearning.js for the hook that feeds it live progress.
//
// None of the underlying storage schemas carry timestamps, so "most recent"
// isn't available. Instead this uses the best deterministic signal each
// schema actually has:
//   1. A partially-completed activity (kanji marked "learning", i.e. seen but
//      not yet known) always wins - it's the one true "still in progress"
//      signal in the data.
//   2. Otherwise, whichever feature has the most established progress (most
//      items with any progress recorded) wins. Comparing raw counts across
//      different units (words learned vs. practice reps) is an approximation,
//      not a normalized score - acceptable here because it's deterministic
//      and there's no richer signal to rank against.
// The learner's JLPT level isn't used to pick *between* features - it
// doesn't need to be, because every destination page already reads the same
// stored level preference (useJlptLevel) and filters itself accordingly.

import { PRACTICE_GOAL } from "../consts/practiceGoal";

function countByValue(record, value) {
    if (!record || typeof record !== "object") return 0;
    let count = 0;
    for (const key in record) {
        if (Object.hasOwn(record, key) && record[key] === value) count += 1;
    }
    return count;
}

function countKeys(record) {
    if (!record || typeof record !== "object") return 0;
    return Object.keys(record).length;
}

function sumValues(record) {
    if (!record || typeof record !== "object") return 0;
    let total = 0;
    for (const key in record) {
        if (!Object.hasOwn(record, key)) continue;
        const value = record[key];
        if (typeof value === "number" && Number.isFinite(value)) total += value;
    }
    return total;
}

// Mirrors the per-character "X/100 reps" goal already shown on the writing
// practice pages themselves (see PracticeProgress + PRACTICE_GOAL), applied
// here as an average across every character that's been drawn at least
// once - not against the full kana/kanji list, which would swamp this to
// ~0% for anyone who hasn't practiced most of the (2,974-kanji) set.
function averageRepsPercent(drawCounts, total) {
    const practicedCharCount = countKeys(drawCounts);
    if (practicedCharCount === 0) return 0;
    const average = total / practicedCharCount;
    return Math.min(100, Math.round((average / PRACTICE_GOAL) * 100));
}

function pluralize(count, noun) {
    return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

/**
 * @param {object} signals
 * @param {object} signals.kanjiProgress - { [symbol]: "known" | "learning" }
 * @param {object} signals.grammarProgress - { [pointId]: "learned" }
 * @param {object} signals.vocabularyProgress - { [wordId]: "learned" }
 * @param {object} signals.kanjiDrawCounts - { [symbol]: number }
 * @param {object} signals.kanaDrawCounts - { [symbol]: number }
 * @returns {{ primary: object, secondary: object[] } | null}
 */
export function resolveContinueLearning(signals = {}) {
    const kanjiProgress = signals.kanjiProgress;
    const grammarProgress = signals.grammarProgress;
    const vocabularyProgress = signals.vocabularyProgress;
    const kanjiDrawCounts = signals.kanjiDrawCounts;
    const kanaDrawCounts = signals.kanaDrawCounts;

    const kanjiLearningCount = countByValue(kanjiProgress, "learning");
    const kanjiKnownCount = countByValue(kanjiProgress, "known");
    const grammarLearnedCount = countKeys(grammarProgress);
    const vocabularyLearnedCount = countKeys(vocabularyProgress);
    const kanjiDrawTotal = sumValues(kanjiDrawCounts);
    const kanaDrawTotal = sumValues(kanaDrawCounts);

    const candidates = [];

    // Priority 1: partially completed. Kanji marked "learning" is the only
    // schema in the app that distinguishes "started" from "done".
    if (kanjiLearningCount > 0) {
        candidates.push({
            key: "kanji-memory-quiz",
            priority: 1,
            weight: kanjiLearningCount,
            to: "/kanji-memory-quiz",
            title: "Kanji Memory Quiz",
            detail: "Continue your last session",
        });
    }

    // Priority 2: established (non-partial) progress, ranked by item count.
    if (grammarLearnedCount > 0) {
        candidates.push({
            key: "grammar",
            priority: 2,
            weight: grammarLearnedCount,
            to: "/grammar",
            title: "Japanese Grammar",
            detail: `${pluralize(grammarLearnedCount, "grammar point")} learned`,
        });
    }

    if (vocabularyLearnedCount > 0) {
        candidates.push({
            key: "vocabulary",
            priority: 2,
            weight: vocabularyLearnedCount,
            to: "/vocabulary",
            title: "Japanese Vocabulary",
            detail: `${pluralize(vocabularyLearnedCount, "word")} learned`,
        });
    }

    // Only add a kanji-quiz candidate here if it didn't already qualify
    // above at priority 1 (avoids double-counting the same feature).
    if (kanjiLearningCount === 0 && kanjiKnownCount > 0) {
        candidates.push({
            key: "kanji-memory-quiz",
            priority: 2,
            weight: kanjiKnownCount,
            to: "/kanji-memory-quiz",
            title: "Kanji Memory Quiz",
            detail: "Continue your last session",
        });
    }

    if (kanjiDrawTotal > 0) {
        candidates.push({
            key: "kanji-writing-practice",
            priority: 2,
            weight: kanjiDrawTotal,
            to: "/kanji-writing-practice",
            title: "Kanji Writing Practice",
            detail: `${pluralize(kanjiDrawTotal, "practice rep")} so far`,
            percent: averageRepsPercent(kanjiDrawCounts, kanjiDrawTotal),
        });
    }

    if (kanaDrawTotal > 0) {
        candidates.push({
            key: "kana-writing-practice",
            priority: 2,
            weight: kanaDrawTotal,
            to: "/kana-writing-practice",
            title: "Kana Writing Practice",
            detail: `${pluralize(kanaDrawTotal, "practice rep")} so far`,
            percent: averageRepsPercent(kanaDrawCounts, kanaDrawTotal),
        });
    }

    if (!candidates.length) return null;

    candidates.sort((a, b) => a.priority - b.priority || b.weight - a.weight);

    const [primary, ...rest] = candidates;
    return { primary, secondary: rest.slice(0, 2) };
}
